/**
 * Імпорт банківської виписки — «розумний розбір» (ч.725, клієнт-банк етап 1).
 *
 * Модель власника: бухгалтер НЕ набиває і НЕ привʼязує кожну платіжку —
 * він ПІДТВЕРДЖУЄ. Сервер розпізнає контрагента (ЄДРПОУ/IBAN/правило),
 * договір (основний/єдиний), рахунок-підставу з призначення платежу і нашу
 * платіжку (сума+контрагент+дата). Рядки діляться на «розпізнано» (одна кнопка
 * «створити і провести») та «потребують уваги» (швидкий вибір + «запамʼятати
 * як правило» → наступна виписка розпізнається сама).
 * Формати: канонічний CSV, ПриватБанк CSV, 1С Клієнт-банк (txt, Приват24/ПУМБ).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DatePopover from '@/components/DatePopover';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StoreApi, UseBoundStore } from 'zustand';
import ContractorCodeHint from '@/shared/components/ContractorCodeHint';
import {
  type Direction,
  bankImport,
  type ImportApplyResult,
  type ImportRowApply,
  type StatementFormat,
  type StatementPreview,
  type StatementRow,
} from '@/api/bankImport';
import { contractors as contractorsApi, type ContractorCreate, type ContractorType } from '@/api/catalogs';
import { refs, type StatementFormatInfo } from '@/api/refs';
import { bankPayments, bankReceipts } from '@/api/bankDocs';
import { customerOrders, supplierOrders } from '@/api/orders';
import { reports as reportsApi, type SettlementByDocumentRow } from '@/api/documents';
import SearchSelect from '@/components/SearchSelect';
import { useColumnWidths } from '@/shared/hooks/useColumnWidths';
import { DocActionBar } from '@/shared/components/DocumentToolbar';
import { ai as aiApi } from '@/api/ai';
import { aiAvailableText } from '@/shared/utils/aiCredits';
import { useConfirm } from '@/components/ConfirmProvider';
import { useToast } from '@/components/ToastProvider';
import { extractApiError } from '@/utils/extractApiError';
import QuickContractModal from '@/shared/components/QuickContractModal';
import { useAllPermissionsByOrg, usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import AiBadge from '@/shared/components/AiBadge';
import { autoCfiId, OWNER_FUNDS_OPS, opsForOrg } from '@/shared/utils/cfiByOp';
import BankImportRulesModal from '@/pages/BankImportRulesModal';
import { contractLabel } from '@/shared/utils/labels';
import {
  type BankImportSessionState, useBankImportSession, useBankReviewSession,
} from '@/store/bankImportSession';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ч.790: той самий набір типів, що у редакторі платіжки (BankDocEditor).
const RECEIPT_OPS = ['FROM_BUYER', 'FROM_BANK', 'OTHER', 'LOAN', 'LOAN_GIVEN_RETURN', 'TRANSIT_IN', 'CARD_RECEIPT', 'CURRENCY_EXCHANGE', 'REFUND_FROM_SUPPLIER', 'TRANSFER_OWN', 'CAPITAL_CONTRIBUTION', 'OWNER_CONTRIBUTION'];
// ч.828: види без договору (податки — бюджет, ЗП/підзвіт — працівник, переказ — свої рахунки;
// ч.1133: власні кошти підприємця — ФОП сам собі).
const NO_CONTRACT_OPS = ['TAX', 'SALARY', 'TRANSFER_OWN', 'TO_PODOTCHET', 'CAPITAL_CONTRIBUTION',
  'OWNER_WITHDRAWAL', 'OWNER_CONTRIBUTION'];
/**
 * Чи є в рядка сторона — або вона не потрібна. Переказ між своїми рахунками
 * сторони не має; податок до бюджету (ч.1132) — теж: отримувач там Казначейство,
 * заводити його контрагентом заради кожної платіжки не треба (те саме правило —
 * на сервері, recognition_state). Зарплата і підзвіт — працівник.
 */
export function partyReady(r: { operation_type?: string | null; employee_id?: string | null;
  contractor_id?: string | null; direction?: string | null }): boolean {
  if (r.operation_type === 'TRANSFER_OWN') return true;
  if (r.operation_type && OWNER_FUNDS_OPS.includes(r.operation_type)) return true;  // ч.1133
  if (r.operation_type === 'TAX' && r.direction !== 'IN') return true;
  if (r.operation_type === 'SALARY' || r.operation_type === 'TO_PODOTCHET') return !!r.employee_id;
  return !!r.contractor_id;
}
const PAYMENT_OPS = ['TO_SUPPLIER', 'TO_BANK', 'TAX', 'SALARY', 'LOAN_RETURN', 'LOAN_GIVEN', 'TRANSIT_OUT', 'OTHER', 'CARD_PAYMENT', 'CURRENCY_EXCHANGE', 'REFUND_TO_BUYER', 'TRANSFER_OWN', 'TO_PODOTCHET', 'OWNER_WITHDRAWAL'];
const OP_LABEL: Record<string, string> = {
  FROM_BUYER: 'від покупця', FROM_BANK: 'від банку', OTHER: 'інше', LOAN: 'отримання позики',
  CARD_RECEIPT: 'карткою (еквайринг)',
  TO_SUPPLIER: 'постачальнику', TO_BANK: 'у банк', TAX: 'податки', SALARY: 'зарплата',
  LOAN_RETURN: 'повернення отриманої позики', LOAN_GIVEN: 'надання позики', LOAN_GIVEN_RETURN: 'повернення наданої позики',
  TRANSIT_IN: 'транзит (комісія/доручення)', TRANSIT_OUT: 'транзит — перерахування комітенту',
  CARD_PAYMENT: 'картковий платіж',
  REFUND_FROM_SUPPLIER: 'повернення від постачальника', REFUND_TO_BUYER: 'повернення покупцю',
  TRANSFER_OWN: 'переказ між своїми рахунками', TO_PODOTCHET: 'у підзвіт',
  CAPITAL_CONTRIBUTION: 'внесок до статутного капіталу',
  CURRENCY_EXCHANGE: 'купівля/продаж валюти',
  OWNER_WITHDRAWAL: 'вилучення коштів підприємцем',
  OWNER_CONTRIBUTION: 'внесення власних коштів підприємця',
};

// ч.815: колір рамки поля, якого бракує для повного розпізнавання (як смужка «частково»).
const MISSING = '#f59e0b';
// ч.824: поле, заповнене Oblik+/AI (до прийняття ✓) — бузкова рамка.
const AI_COLOR = '#7c3aed';

interface RowState extends ImportRowApply {
  parse_error: string | null;
  match_confidence: string;
  counterparty_name: string | null;
  duplicate_of_id: string | null;
  duplicate_of_kind: 'receipt' | 'payment' | null;
  duplicate_of_number: string | null;
  recognized: boolean;
  matched_contract_name: string | null;
  matched_order_number: string | null;
  matched_payment_order_number: string | null;
  matched_bank_payment_number: string | null;  // ч.727
  rule_id: string | null;
  basis_label: string | null;   // ч.790: підпис обраної накладної-підстави
  matched_employee_name: string | null;  // ч.810
  // ч.819: підказка помічника Oblik+/AI — лишається «частково» до «прийняти».
  assisted?: boolean;
  assist_reason?: string | null;
  assist_confidence?: number | null;
  assist_fields?: string[];  // які поля заповнив помічник (бузкова рамка)
  cfi_auto: string | null;      // ч.790: стаття ДДС, підставлена авто (щоб не затирати ручну)
}

/** ч.833: режим «нерозпізнані» — та сама таблиця, але рядки = існуючі документи
 *  (needs_review) організації; вбудовується у вкладку /bank-payments, /bank-receipts. */
export interface BankImportPageProps {
  mode?: 'review';
  direction?: Direction;
  orgId?: string;
  onApplied?: () => void;
}

/**
 * ч.1231: формат файла виписки — за рахунком. Ручний вибір діє, лише якщо такий формат є
 * серед форматів ЦЬОГО рахунку (після зміни рахунку вибір попереднього не «прилипає»);
 * інакше — перший формат рахунку (за замовчуванням).
 */
export function pickStatementFormat(
  formats: StatementFormatInfo[], choice: string,
): StatementFormatInfo | undefined {
  return formats.find((f) => f.code === choice) ?? formats[0];
}

/** ч.1231: підключення банку каже «розпізнані не проводити» → основна дія — чернетки. */
export function draftsAreDefault(
  preview: { auto_post_recognized?: boolean | null } | null | undefined, isReview: boolean,
): boolean {
  return !isReview && preview?.auto_post_recognized === false;
}

// ч.1294: колонки таблиці розпізнавання — ширини за замовчуванням (px); користувач
// тягне межу заголовка, своє зберігається у профілі (uiPrefs). Разом ≈ 1600 px: на
// широкому екрані колонки пропорційно ширшають, на вузькому — горизонтальна прокрутка.
const BANK_IMPORT_COLS: Record<string, number> = {
  status: 30, line: 58, date: 86, dir: 40, amount: 96, party: 240, contract: 200,
  basis: 210, op: 150, cfi: 180, purpose: 260, remember: 84,
};

export default function BankImportPage({ mode, direction: reviewDirection, orgId: reviewOrgId, onApplied }: BankImportPageProps = {}) {
  const isReview = mode === 'review';
  const qc = useQueryClient();
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('bank.payment.manage');

  // ч.1354: вибір рахунку/джерела, розібрана виписка, правки рядків і підказки Oblik+/AI —
  // у сховищі поза сторінкою (`store/bankImportSession`): перехід на іншу сторінку і назад
  // показує те саме, відповідь агента лягає і без сторінки. Режим «нерозпізнані» — окреме,
  // незбережене сховище (рядки там — документи з сервера).
  type Session = UseBoundStore<StoreApi<BankImportSessionState<StatementPreview, RowState, ImportApplyResult>>>;
  const useSession = (isReview ? useBankReviewSession : useBankImportSession) as unknown as Session;
  const orgId = useSession((st) => st.orgId);
  const setOrgId = useSession((st) => st.setOrgId);
  useEffect(() => { if (reviewOrgId && orgId !== reviewOrgId) setOrgId(reviewOrgId); }, [reviewOrgId]);  // eslint-disable-line react-hooks/exhaustive-deps
  // ч.1133: «власні кошти підприємця» пропонуємо лише для організації-ФОП; вид, який
  // у рядку вже стоїть (його визначив розбір), лишаємо у списку.
  const orgKind = availableOrgs.find((o) => o.id === orgId)?.entity_type;
  const rowOps = (ops: string[], current: string | null | undefined): string[] => {
    const list = opsForOrg(ops, orgKind);
    return current && !list.includes(current) ? [...list, current] : list;
  };
  useEffect(() => { if (isReview) { setOrgId(reviewOrgId ?? ''); setPreview(null); setRows([]); } }, [isReview, reviewOrgId]);
  const bankId = useSession((st) => st.bankId);
  const setBankId = useSession((st) => st.setBankId);
  const [file, setFile] = useState<File | null>(null);
  // ч.1231 (рішення власника): формат файла виписки підтягується ЗА РАХУНКОМ (банк — з IBAN);
  // вручну обирається лише тоді, коли для банку рахунку форматів кілька.
  const formatChoice = useSession((st) => st.formatChoice);
  const setFormatChoice = useSession((st) => st.setFormatChoice);
  // ч.795 (рішення власника «джерела два — функціонал один»): виписка з API
  // банку йде через той самий розбір. ?source=api&org=… — з «Підключення банків».
  const [sp] = useSearchParams();
  const source = useSession((st) => st.source);
  const setSource = useSession((st) => st.setSource);
  useEffect(() => { if (sp.get('source') === 'api') setSource('api'); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const apiFrom = useSession((st) => st.apiFrom);
  const setApiFrom = useSession((st) => st.setApiFrom);
  const apiTo = useSession((st) => st.apiTo);
  const setApiTo = useSession((st) => st.setApiTo);
  const [rulesOpen, setRulesOpen] = useState(false);

  const preview = useSession((st) => st.preview);
  const setPreview = useSession((st) => st.setPreview);
  const rows = useSession((st) => st.rows);
  const setRows = useSession((st) => st.setRows);
  // ч.940: текст помилки розбору (показуємо банером над формою).
  const [previewError, setPreviewError] = useState<string | null>(null);
  const applyResult = useSession((st) => st.applyResult);
  const setApplyResult = useSession((st) => st.setApplyResult);
  // ч.727: статті ДДС для колонки (системні платформи + власні).
  const cfiQ = useQuery({ queryKey: ['cash-flow-items'], queryFn: refs.cashFlowItems, staleTime: 300_000 });

  if (!orgId && availableOrgs[0]) setOrgId(sp.get('org') && availableOrgs.some((o) => o.id === sp.get('org')) ? sp.get('org')! : availableOrgs[0].id);

  const banksQ = useQuery({
    queryKey: ['bank-accounts', orgId],
    queryFn: () => refs.bankAccounts(orgId),
    enabled: !!orgId,
  });
  // ч.1268: з «Підключення до банку» (?source=api) одразу беремо рахунок, що має підключення.
  if (!bankId && banksQ.data?.[0]) {
    setBankId((source === 'api' && banksQ.data.find((b) => b.has_api_connection)?.id) || banksQ.data[0].id);
  }
  // Джерело «API банку» — лише для рахунку з увімкненим підключенням (рішення власника).
  const accountHasApi = !!banksQ.data?.find((b) => b.id === bankId)?.has_api_connection;
  if (source === 'api' && bankId && banksQ.data && !accountHasApi) setSource('');
  const accountFormats = useMemo(
    () => banksQ.data?.find((b) => b.id === bankId)?.statement_formats ?? [],
    [banksQ.data, bankId]);
  const formatInfo = pickStatementFormat(accountFormats, formatChoice);
  const format = (formatInfo?.code ?? '') as StatementFormat | '';

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const contractorsList = useMemo(() => contractorsQ.data?.items ?? [], [contractorsQ.data]);
  const contractsQ = useQuery({
    queryKey: ['contracts-org', orgId],
    queryFn: () => refs.contracts({ organization_id: orgId }),
    enabled: !!orgId,
  });
  const contractsByCtr = useMemo(() => {
    const m = new Map<string, { id: string; label: string }[]>();
    (contractsQ.data ?? []).forEach((c) => {
      const arr = m.get(c.contractor_id) ?? [];
      arr.push({ id: c.id, label: contractLabel(c) });
      m.set(c.contractor_id, arr);
    });
    return m;
  }, [contractsQ.data]);

  const previewMut = useMutation({
    mutationFn: () => isReview
      ? bankImport.review(orgId, reviewDirection ?? 'OUT')
      : source === 'api'
      ? bankImport.previewApi(orgId, bankId, apiFrom, apiTo)
      : bankImport.preview(orgId, bankId, file!, format as StatementFormat),
    onSuccess: (p) => {
      setPreview(p);
      setApplyResult(null);
      setPreviewError(null);
      setRows(p.rows.map((r) => statementRowToState(r)));
    },
    onError: (e: unknown) => {
      // ч.940: помилка розбору (зокрема «виписка не за обраним рахунком») —
      // тостом і банером над формою, а не alert'ом (Закон дизайну #7).
      const msg = extractApiError(e);
      setPreviewError(msg);
      toast.show({ type: 'error', message: msg, durationMs: 9000 });
    },
  });

  // ч.833: у режимі «нерозпізнані» рядки тягнемо одразу (і після кожного застосування).
  const previewMutate = previewMut.mutate;
  useEffect(() => {
    if (isReview && orgId) previewMutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReview, orgId, reviewDirection]);

  const toApply = (rs: RowState[]): ImportRowApply[] => rs.map(({
    parse_error, match_confidence, counterparty_name,
    duplicate_of_id, duplicate_of_kind, duplicate_of_number,
    recognized, matched_contract_name, matched_order_number,
    matched_payment_order_number, rule_id, matched_bank_payment_number,
    basis_label, cfi_auto, matched_employee_name, assisted, assist_reason, assist_confidence, assist_fields,
    ...r
  }) => {
    void parse_error; void match_confidence; void counterparty_name;
    void duplicate_of_id; void duplicate_of_kind; void duplicate_of_number;
    void recognized; void matched_contract_name; void matched_order_number;
    void matched_payment_order_number; void rule_id; void matched_bank_payment_number;
    void basis_label; void cfi_auto; void matched_employee_name; void assisted; void assist_reason; void assist_confidence; void assist_fields;
    return r;
  });

  const toast = useToast();
  const applyMut = useMutation({
    mutationFn: (postRecognized: boolean) => bankImport.apply(
      orgId, isReview ? null : bankId,
      toApply(rows.map((r) => ({ ...r, post: postRecognized && isFull(r) && !r.skip,
        needs_review: !isFull(r) }))),
    ),
    onSuccess: (res) => {
      setApplyResult(res);
      qc.invalidateQueries({ queryKey: ['bank-match-rules', orgId] });
      // ч.931 (зауваження власника): системне повідомлення вгорі + опрацьовані
      // рядки (документ створено/підтверджено/оновлено або пропущено як дубль)
      // зникають з переліку — лишаються лише непозначені та з помилкою.
      const parts: string[] = [];
      if ((res.reviewed ?? 0) > 0) parts.push(`оновлено ${res.reviewed}`);
      if (res.created_receipts > 0) parts.push(`створено надходжень ${res.created_receipts}`);
      if (res.created_payments > 0) parts.push(`створено списань ${res.created_payments}`);
      if (res.posted > 0) parts.push(`проведено ${res.posted}`);
      if ((res.bank_payments_confirmed ?? 0) > 0) parts.push(`підтверджено чернеток ${res.bank_payments_confirmed}`);
      if (res.skipped_as_duplicate > 0) parts.push(`дублів пропущено ${res.skipped_as_duplicate}`);
      if (res.rules_saved > 0) parts.push(`правил запамʼятовано ${res.rules_saved}`);
      const summary = parts.length ? parts.join(', ') : 'нічого не створено';
      if (res.errors.length) {
        toast.show({ type: 'warning', message: `Імпорт: ${summary}; помилок ${res.errors.length} — деталі внизу сторінки` });
      } else {
        toast.show({ type: 'success', message: `Імпорт: ${summary}` });
      }
      if (isReview) { onApplied?.(); previewMutate(); return; }
      const gone = new Set([...(res.done_lines ?? []), ...(res.duplicate_lines ?? [])]);
      if (gone.size) setRows((rs) => rs.filter((r) => !gone.has(r.line_number)));
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });

  // ч.1231: підключення банку каже «розпізнані не проводити» → основна кнопка — чернетки.
  const draftsFirst = draftsAreDefault(preview, isReview);

  // ч.830: без виду операції документ не створюється — зупиняємо до відправки.
  function runApply(postRecognized: boolean) {
    const missing = rows.filter((r) => !r.skip && !r.parse_error && !r.operation_type).map((r) => r.line_number);
    if (missing.length) {
      toast.show({ type: 'error', message: `Вкажіть вид операції у рядках: ${missing.slice(0, 10).join(', ')}${missing.length > 10 ? '…' : ''}` });
      return;
    }
    applyMut.mutate(postRecognized);
  }
  function updateRow(idx: number, patch: Partial<RowState>) {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  // ч.790: стаття ДДС авто за типом операції (як у платіжці): підставляємо у
  // порожню або раніше авто-підставлену; ручний вибір не чіпаємо.
  const cfiItems = cfiQ.data;
  useEffect(() => {
    if (!cfiItems || rows.length === 0) return;
    setRows((rs) => {
      let changed = false;
      const next = rs.map((r) => {
        if (r.cash_flow_item_id) return r;
        const auto = autoCfiId(cfiItems, r.operation_type);
        if (!auto) return r;
        changed = true;
        return { ...r, cash_flow_item_id: auto, cfi_auto: auto };
      });
      return changed ? next : rs;
    });
  }, [cfiItems, rows.length]);
  function changeOp(idx: number, op: string) {
    const r = rows[idx];
    if (!r) return;
    const auto = autoCfiId(cfiItems, op);
    const patch: Partial<RowState> = { operation_type: op };
    if (NO_CONTRACT_OPS.includes(op)) patch.contract_id = null;  // ч.828
    if (auto && (!r.cash_flow_item_id || r.cash_flow_item_id === r.cfi_auto)) {
      patch.cash_flow_item_id = auto; patch.cfi_auto = auto;
    }
    updateRow(idx, patch);
  }
  const confirm = useConfirm();
  const [newCtrFor, setNewCtrFor] = useState<number | null>(null);
  const [newContractFor, setNewContractFor] = useState<number | null>(null);  // ч.830
  // ч.810: «розпізнано» = є контрагент/працівник + вид операції ≠ інше + стаття
  // ДДС + не дубль. Рахуємо на клієнті після кожної правки (бек дає початкове).
  const employeesQ = useQuery({
    queryKey: ['employees-active', orgId],
    queryFn: () => refs.employees(orgId, { active_only: true }),
    enabled: !!orgId,
  });
  const employeeItems = useMemo(() => (employeesQ.data ?? []).map((e) => ({
    id: e.id, label: e.name, hint: e.code ?? null,
  })), [employeesQ.data]);
  const cfiSearchItems = useMemo(() => (cfiQ.data ?? []).map((x) => ({
    id: x.id, label: x.name, hint: x.code ?? null,
  })), [cfiQ.data]);
  function rowReasons(r: RowState): string[] {
    if (r.parse_error) return ['помилка розбору'];
    const out: string[] = [];
    if (r.duplicate_of_id) out.push('можливий дубль');
    const empOp = r.operation_type === 'SALARY' || r.operation_type === 'TO_PODOTCHET';
    if (!partyReady(r)) out.push(empOp ? 'працівник' : 'контрагент');
    if (!r.operation_type) out.push('вид операції');  // ч.830: «інше» — свідомий вибір
    if (!r.cash_flow_item_id) out.push('стаття ДДС');
    return out;
  }
  const isFull = (r: RowState) => rowReasons(r).length === 0 && !r.assisted;
  // ч.825: ✓ біля КОЖНОГО поля, яке заповнив помічник; коли прийнято всі —
  // рядок перестає бути «від помічника» і запамʼятовується правилом.
  function acceptField(i: number, field: string) {
    const r = rows[i];
    if (!r) return;
    const left = (r.assist_fields ?? []).filter((f) => f !== field);
    updateRow(i, left.length === 0
      ? { assist_fields: [], assisted: false, remember_rule: !!(r.counterparty_edrpou || r.counterparty_iban) }
      : { assist_fields: left });
  }
  const aiCheck = (r: RowState, i: number, field: string) => r.assist_fields?.includes(field) ? (
    <button type="button"
      style={{ padding: '0 5px', fontSize: '0.8rem', color: AI_COLOR, borderColor: AI_COLOR, fontWeight: 700, flex: '0 0 auto' }}
      title={`Прийняти це поле від Oblik+/AI (${Math.round((r.assist_confidence ?? 0) * 100)}%): ${r.assist_reason ?? ''}`}
      onClick={() => acceptField(i, field)}>✓</button>
  ) : null;
  // ч.819: помічник Oblik+/AI — лише для рядків, де чогось бракує; підказки заповнюють
  // поля, але рядок лишається «частково» (assisted) до «прийняти». Доступний усім
  // (згода — в умовах угоди, рішення власника 2026-09-14).
  const assistBusy = useSession((st) => st.assistBusy);
  const setAssistBusy = useSession((st) => st.setAssistBusy);
  // ч.1098: помічник витрачає кредити акаунта — окреме право групи «Oblik+/AI».
  const { permissionsByOrg } = useAllPermissionsByOrg();
  const canAssist = (permissionsByOrg[orgId] ?? []).includes('ai.bank_assistant.use');
  // ч.1104: баланс кредитів — лише тим, хто може користуватись помічником.
  const aiBalanceQ = useQuery({
    queryKey: ['ai-balance'], queryFn: aiApi.balance, staleTime: 60_000, enabled: canAssist,
  });
  const assistNote = useSession((st) => st.assistNote);
  const setAssistNote = useSession((st) => st.setAssistNote);
  async function runAssistant() {
    const targets = rows.filter((r) => !r.skip && !r.parse_error && rowReasons(r).some((x) => x !== 'можливий дубль'));
    if (targets.length === 0) { setAssistNote('Усі рядки вже розпізнано — Oblik+/AI нема з чим працювати.'); return; }
    setAssistBusy(true); setAssistNote(null);
    try {
      const res = await bankImport.assist(orgId, targets.map((r) => ({
        line_number: r.line_number, direction: r.direction, date: r.date, amount: r.amount,
        purpose: r.purpose ?? null, contractor_id: r.contractor_id ?? null, employee_id: r.employee_id ?? null,
        operation_type: r.operation_type, cash_flow_item_id: r.cash_flow_item_id ?? null,
        counterparty_edrpou: r.counterparty_edrpou ?? null, counterparty_iban: r.counterparty_iban ?? null,
        counterparty_name: r.counterparty_name,
      })));
      const byLine = new Map(res.suggestions.map((s) => [s.line_number, s]));
      let applied = 0;
      setRows((rs) => rs.map((r) => {
        const s = byLine.get(r.line_number);
        if (!s) return r;
        applied += 1;
        const isSalary = s.operation_type === 'SALARY';
        const fields: string[] = [];
        if (isSalary ? (s.employee_id && s.employee_id !== r.employee_id) : (s.contractor_id && s.contractor_id !== r.contractor_id)) fields.push(isSalary ? 'employee' : 'contractor');
        if (s.operation_type && s.operation_type !== r.operation_type) fields.push('operation_type');
        if (s.cash_flow_item_id && s.cash_flow_item_id !== r.cash_flow_item_id) fields.push('cfi');
        return {
          ...r,
          contractor_id: isSalary ? null : (s.contractor_id ?? r.contractor_id ?? null),
          employee_id: isSalary ? (s.employee_id ?? r.employee_id ?? null) : r.employee_id ?? null,
          operation_type: s.operation_type || r.operation_type,
          cash_flow_item_id: s.cash_flow_item_id ?? r.cash_flow_item_id ?? null,
          assisted: fields.length > 0,
          assist_fields: fields,
          assist_reason: s.reason + (s.contractor_new_name && !s.contractor_id ? ` Новий контрагент: «${s.contractor_new_name}» (створіть через «+»).` : ''),
          assist_confidence: s.confidence,
          ai_suggestion: { contractor_id: s.contractor_id, employee_id: s.employee_id,
            operation_type: s.operation_type, cash_flow_item_id: s.cash_flow_item_id },
          remember_rule: false,
        };
      }));
      const esc = res.escalated_rows ? `; ${res.escalated_rows} складних рядків розібрано точнішим розбором` : '';
      setAssistNote(`Oblik+/AI запропонував ${applied} з ${targets.length} рядків (списано ${res.charged_credits.toFixed(2)} кредитів${esc}). Перевірте і натисніть ✓ біля кожного заповненого поля, щоб прийняти.`);
      qc.invalidateQueries({ queryKey: ['ai-balance'] });
    } catch (e: any) {
      setAssistNote(e?.response?.data?.detail ?? 'Oblik+/AI недоступний');
    } finally {
      setAssistBusy(false);
    }
  }
  const contractorItems = useMemo(() => contractorsList.map((c) => ({
    id: c.id, label: c.name, hint: c.edrpou ?? c.inn ?? null,
  })), [contractorsList]);
  async function removeChecked() {
    const checked = rows.filter((r) => !r.skip && !r.parse_error);
    const n = checked.length;
    if (n === 0) return;
    if (isReview) {
      // ч.834 (факт власника): у «нерозпізнаних» рядки — існуючі документи, «видалити»
      // видаляє самі документи (чернетки), а не лише прибирає з переліку.
      if (!await confirm(`Видалити ${n} позначених документів (чернеток)? Дію не скасувати.`)) return;
      const errs: string[] = [];
      for (const r of checked) {
        if (!r.review_doc_id) continue;
        try {
          if (r.direction === 'IN') await bankReceipts.remove(r.review_doc_id);
          else await bankPayments.remove(r.review_doc_id);
        } catch (e) {
          const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
          errs.push(`№ ${r.line_number}: ${typeof d === 'string' ? d : 'не вдалося видалити'}`);
        }
      }
      if (errs.length) toast.show({ type: 'error', message: errs.join('; ') });
      onApplied?.();
      previewMutate();
      return;
    }
    if (!await confirm(`Прибрати з переліку ${n} позначених рядків? Документи по них створено не буде.`)) return;
    setRows((rs) => rs.filter((r) => r.skip || !!r.parse_error));
  }

  const activeRows = rows.filter((r) => !r.skip && !r.parse_error);
  // ч.832 (рішення власника): сортування колонок + посторінковий вивід рядків.
  // Рядки живуть у памʼяті сторінки до «створити» (не в БД) — тому сторінки/сорт
  // на клієнті, індекс `i` лишається індексом у `rows` для updateRow.
  type SortKey = 'status' | 'line' | 'date' | 'dir' | 'amount' | 'party' | 'contract' | 'op' | 'cfi' | 'purpose' | 'remember';
  const [sortKey, setSortKey] = useState<SortKey>('line');
  const colW = useColumnWidths('dt-widths:bank-import', BANK_IMPORT_COLS);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [pageSize, setPageSize] = useState<number>(0);  // 0 = усі (за замовч., рішення власника)
  const [page, setPage] = useState(0);
  const ctrNameById = useMemo(() => new Map(contractorsList.map((c) => [c.id, c.name])), [contractorsList]);
  const cfiNameById = useMemo(() => new Map((cfiQ.data ?? []).map((x) => [x.id, x.name])), [cfiQ.data]);
  const contractLabelById = useMemo(() => {
    const m = new Map<string, string>();
    contractsByCtr.forEach((arr) => arr.forEach((c) => m.set(c.id, c.label)));
    return m;
  }, [contractsByCtr]);
  function sortValue(r: RowState, key: SortKey): string | number {
    switch (key) {
      case 'status': return r.parse_error ? 0 : r.duplicate_of_id ? 1 : rowReasons(r).length === 0 ? 3 : 2;
      case 'line': return r.line_number;
      case 'date': return r.date ?? '';
      case 'dir': return r.direction;
      case 'amount': return Number(r.amount) || 0;
      case 'party': return (r.contractor_id ? ctrNameById.get(r.contractor_id) : null) ?? r.matched_employee_name ?? r.counterparty_name ?? '';
      case 'contract': return (r.contract_id ? contractLabelById.get(r.contract_id) : '') ?? '';
      case 'op': return r.operation_type ? (OP_LABEL[r.operation_type] ?? r.operation_type) : '';
      case 'cfi': return (r.cash_flow_item_id ? cfiNameById.get(r.cash_flow_item_id) : '') ?? '';
      case 'purpose': return r.purpose ?? '';
      case 'remember': return r.remember_rule ? 1 : 0;
      default: return r.line_number;
    }
  }
  const orderedRows = useMemo(() => {
    const idx = rows.map((r, i) => ({ r, i }));
    const col = new Intl.Collator('uk');
    idx.sort((a, b) => {
      const va = sortValue(a.r, sortKey), vb = sortValue(b.r, sortKey);
      const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : col.compare(String(va), String(vb));
      return (sortDir === 'asc' ? c : -c) || a.r.line_number - b.r.line_number;
    });
    return idx;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir, ctrNameById, cfiNameById, contractLabelById]);
  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(orderedRows.length / pageSize)) : 1;
  const curPage = Math.min(page, pageCount - 1);
  const pageRows = pageSize > 0 ? orderedRows.slice(curPage * pageSize, (curPage + 1) * pageSize) : orderedRows;
  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(0);
  }
  const Th = ({ k, col, children, style, title }: {
    k?: SortKey; col: string; children: React.ReactNode; style?: React.CSSProperties; title?: string;
  }) => (
    <th style={{ cursor: k ? 'pointer' : undefined, userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      title={title ?? (k ? 'Сортувати' : undefined)}
      onClick={() => { if (k && !colW.isResizing()) toggleSort(k); }}>
      {children}{k && sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
      {/* ч.1294: ручка ширини колонки (та сама, що у DataTable). */}
      <span className="data-grid__resizer" title="Потягніть, щоб змінити ширину колонки"
        onMouseDown={(e) => colW.startResize(col, e)} onClick={(e) => e.stopPropagation()} />
    </th>
  );
  const recognizedActive = activeRows.filter((r) => isFull(r)).length;
  // ч.815: «виділити» за статусом — галка = рядок піде у створення.
  function selectBy(mode: 'all' | 'full' | 'partial' | 'dup' | 'none') {
    setRows((rs) => rs.map((r) => {
      if (r.parse_error) return r;
      const party = partyReady(r);
      const full = isFull(r);
      const dup = !!r.duplicate_of_id;
      const pick = mode === 'all' ? true : mode === 'none' ? false
        : mode === 'full' ? full : mode === 'dup' ? dup : (party && !full && !dup);
      return { ...r, skip: !pick, allow_duplicate: pick && dup };
    }));
  }

  if (isReview && !orgId) {
    return <div style={{ color: 'var(--muted)', padding: '0.75rem 0' }}>Оберіть організацію, щоб побачити нерозпізнані документи.</div>;
  }

  return (
    <div className={isReview ? undefined : 'card'}>
      <div className="toolbar" style={isReview ? { marginBottom: '0.25rem' } : undefined}>
        {!isReview && <h2 style={{ margin: 0 }}>Імпорт банківської виписки<AiBadge /></h2>}
        {isReview && (
          <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
            Документи з виписки, де контрагента / вид операції / статтю ДДС не розпізнано. Заповніть поля (або
            «допомога Oblik+/AI»), поставте галку і натисніть «зберегти і провести розпізнані».
            {previewMut.isPending && ' Завантаження…'}
          </span>
        )}
        <button style={{ marginLeft: 'auto' }} onClick={() => setRulesOpen(true)}
          title="Памʼять розбору: що ви підтвердили для платника/отримувача (ЄДРПОУ/IBAN) — застосовується до наступних виписок">
          правила розпізнавання
        </button>
      </div>

      {rulesOpen && orgId && (
        <BankImportRulesModal
          orgId={orgId}
          opLabel={OP_LABEL}
          cfiName={(id) => (id ? (cfiQ.data?.find((x) => x.id === id)?.name ?? '…') : '—')}
          onClose={() => setRulesOpen(false)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['bank-match-rules', orgId] })}
        />
      )}

      {!isReview && (<>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setBankId(''); setPreview(null); }}>
            {availableOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють імпорт)
            </small>
          )}
          {!hasPermissionInAnyOrg && (
            <small style={{ color: 'var(--badge-amber-fg)', fontSize: '0.75rem' }}>
              ⚠ У вас немає прав імпортувати виписки у жодній організації
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Розрахунковий рахунок</label>
          <select value={bankId} onChange={(e) => { setBankId(e.target.value); setPreview(null); }}>
            <option value="">—</option>
            {banksQ.data?.map((b) => (
              <option key={b.id} value={b.id}>{b.account_name ?? b.iban}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Джерело</label>
          <select value={source} onChange={(e) => { setSource(e.target.value as 'file' | 'api' | ''); setPreview(null); }}>
            <option value="">—</option>
            <option value="file">файл виписки</option>
            {accountHasApi && <option value="api">API банку (підключення)</option>}
          </select>
          {bankId && !accountHasApi && (
            <small data-testid="no-api-hint" style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>
              Отримання виписки з банку доступне для рахунку з підключенням —
              {' '}<Link to="/bank-api">Підключення до банку</Link>.
            </small>
          )}
        </div>
        {/* ч.816 (запит власника): формат/джерело порожні за замовчуванням; кнопка —
            у рядку одразу за файлом (для API — за датами), активна лише коли
            обрано формат і файл; підказки прибрано. */}
        {source === 'file' && (<>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Формат виписки</label>
            {/* ч.1231: формат — за банком обраного рахунку; список активний лише коли форматів кілька. */}
            <select
              value={format}
              disabled={accountFormats.length < 2}
              title={formatInfo?.hint ?? 'Спершу оберіть розрахунковий рахунок'}
              onChange={(e) => { setFormatChoice(e.target.value); setPreview(null); }}
            >
              {accountFormats.length === 0 && <option value="">— оберіть рахунок —</option>}
              {accountFormats.map((f) => (
                <option key={f.code} value={f.code}>{f.label}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Файл виписки</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="file"
                accept={formatInfo?.accept ?? '.csv,.txt'}
                style={{ flex: 1 }}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <button
                className="primary"
                style={{ whiteSpace: 'nowrap' }}
                disabled={!file || !format || !orgId || !bankId || previewMut.isPending}
                onClick={() => { setPreviewError(null); previewMut.mutate(); }}
              >
                {previewMut.isPending ? 'Завантаження…' : 'завантажити та розібрати'}
              </button>
            </div>
          </div>
        </>)}
        {source === 'api' && (
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Період</label>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <DatePopover value={apiFrom} onChange={setApiFrom} style={{ width: 130 }} />
              <span style={{ color: 'var(--muted)' }}>—</span>
              <DatePopover value={apiTo} onChange={setApiTo} style={{ width: 130 }} />
              <button
                className="primary"
                style={{ marginLeft: '0.5rem', whiteSpace: 'nowrap' }}
                disabled={!orgId || !bankId || previewMut.isPending}
                onClick={() => previewMut.mutate()}
              >
                {previewMut.isPending ? 'Завантаження…' : 'отримати з банку та розібрати'}
              </button>
            </div>
          </div>
        )}
      </div>


      </>)}

      {isReview && preview && preview.rows.length === 0 && (
        <div style={{ color: 'var(--muted)', padding: '0.5rem 0' }}>Нерозпізнаних документів немає.</div>
      )}
      {/* ч.940: помилка розбору (напр. «виписка не за обраним рахунком») —
          банер над результатом, з підказкою що робити. */}
      {previewError && !isReview && (
        <div style={{ margin: '0.5rem 0', padding: '0.6rem 0.75rem', borderRadius: 6,
          background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', fontSize: '0.88rem' }}>
          <b>Не вдалося розібрати виписку.</b> {previewError}
          <button type="button" style={{ marginLeft: 8, padding: '0 0.4rem' }}
            onClick={() => setPreviewError(null)}>×</button>
        </div>
      )}

      {preview && (
        <>
          {/* ч.824 (запит власника): компактний рядок підсумків + «допомога Oblik+/AI»
              одразу за «частково»; замість жовтої плашки — стан помічника. */}
          {(() => {
            const live = rows.filter((r) => !r.parse_error);
            const full = live.filter((r) => isFull(r)).length;
            const partial = live.filter((r) => !isFull(r) && !r.duplicate_of_id).length;
            const dup = live.filter((r) => !!r.duplicate_of_id).length;
            return (
              <div style={{ display: 'flex', gap: '0.9rem', alignItems: 'center', flexWrap: 'wrap',
                padding: '0.45rem 0.75rem', background: 'var(--bg-soft-2)', borderRadius: 6, marginBottom: '0.6rem', fontSize: '0.85rem' }}>
                <span>усього <b>{preview.rows.length}</b></span>
                <span style={{ color: 'var(--badge-green-fg)' }}>надходжень <b>{fmt(preview.total_in)}</b></span>
                <span style={{ color: 'var(--badge-red-fg)' }}>списань <b>{fmt(preview.total_out)}</b></span>
                <span style={{ color: 'var(--badge-green-fg)' }}>розпізнано <b>{full}</b></span>
                <span style={{ color: partial > 0 ? '#b45309' : undefined }}>
                  частково <b>{partial}</b>
                  {partial > 0 && (
                    <button type="button" disabled={assistBusy || !canAssist}
                      style={{ marginLeft: 6, color: '#7c3aed', padding: '0 0.5rem', fontSize: '0.8rem' }}
                      title={!canAssist
                        ? 'Немає права «Oblik+/AI: помічник розбору банківської виписки» у цій організації'
                        : `Oblik+/AI підкаже контрагента, вид операції і статтю ДДС для нерозпізнаних рядків. Доступно кредитів: ${aiAvailableText(aiBalanceQ.data) ?? '…'}`}
                      onClick={() => void runAssistant()}>
                      допомога Oblik+/AI
                    </button>
                  )}
                </span>
                <span style={{ color: dup > 0 ? '#b45309' : undefined }}>дублі <b>{dup}</b></span>
              </div>
            );
          })()}
          {assistBusy ? (
            <div style={{ padding: '0.5rem 0.75rem', background: '#f5f3ff', borderRadius: 6,
              marginBottom: '1rem', fontSize: '0.85rem', color: '#6d28d9' }}>
              Oblik+/AI працює<span className="ai-dots"><span>.</span><span>.</span><span>.</span></span>
            </div>
          ) : assistNote ? (
            <div style={{ padding: '0.5rem 0.75rem', background: '#f5f3ff', borderRadius: 6,
              marginBottom: '1rem', fontSize: '0.85rem', color: '#6d28d9' }}>
              {assistNote}
            </div>
          ) : preview.duplicate_count > 0 && (
            <div style={{
              padding: '0.5rem 0.75rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
              marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--badge-amber-fg)' }}>
              ⚠ {preview.duplicate_count} рядків збігаються з уже існуючими документами
              (дата, сума, призначення) — позначені «пропустити». Зніміть позначку, якщо
              документ треба створити попри дубль.
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            {pageSize > 0 && orderedRows.length > pageSize && (
              <>
                <button type="button" disabled={curPage === 0} onClick={() => setPage(curPage - 1)} style={{ padding: '0 0.5rem' }}>‹</button>
                <span>{curPage * pageSize + 1}–{Math.min((curPage + 1) * pageSize, orderedRows.length)} з {orderedRows.length}</span>
                <button type="button" disabled={curPage >= pageCount - 1} onClick={() => setPage(curPage + 1)} style={{ padding: '0 0.5rem' }}>›</button>
              </>
            )}
            {colW.custom && (
              <button type="button" style={{ marginLeft: 'auto', fontSize: '0.75rem' }} onClick={colW.reset}
                title="Повернути ширини колонок таблиці за замовчуванням">ширини колонок — як було</button>
            )}
            <span style={{ marginLeft: colW.custom ? undefined : 'auto' }}>рядків на сторінці:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} style={{ width: 80 }}>
              <option value={0}>усі</option>
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="tch tch--fixed" style={{ minWidth: colW.total }}>
              <colgroup>
                {Object.keys(BANK_IMPORT_COLS).map((c) => <col key={c} style={{ width: colW.widths[c] }} />)}
              </colgroup>
              <thead>
                <tr style={{ textTransform: 'none' }}>
                  <Th k="status" col="status" title="Сортувати за статусом (помилка / дубль / частково / розпізнано)">—</Th>
                  <Th k="line" col="line">#</Th>
                  <Th k="date" col="date">Дата</Th>
                  <Th k="dir" col="dir">↑↓</Th>
                  <Th k="amount" col="amount" style={{ textAlign: 'right' }}>Сума</Th>
                  <Th k="party" col="party">Контрагент</Th>
                  <Th k="contract" col="contract">Договір</Th>
                  <Th col="basis" title="Рахунок/замовлення або неоплачена накладна контрагента; наша платіжка/чернетка, яку закриває це списання">Підстава</Th>
                  <Th k="op" col="op">Тип операції</Th>
                  <Th k="cfi" col="cfi" title="Стаття руху грошових коштів (ДДС); підставляється з правила. Сортувати">Стаття ДДС</Th>
                  <Th k="purpose" col="purpose">Призначення</Th>
                  <Th k="remember" col="remember" title="Запамʼятати контрагента/договір/тип операції для цього ЄДРПОУ (IBAN) — наступна виписка розпізнається сама">запамʼятати</Th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(({ r, i }) => {
                  const ctrContracts = r.contractor_id ? (contractsByCtr.get(r.contractor_id) ?? []) : [];
                  const reasons = rowReasons(r);
                  const partyOk = partyReady(r);
                  const partial = !r.parse_error && partyOk && reasons.some((x) => x !== 'можливий дубль');
                  const stripe = r.parse_error ? '#dc2626' : reasons.length === 0 ? '#16a34a'
                    : partyOk ? '#f59e0b' : '#dc2626';
                  return (
                  <tr
                    key={r.line_number}
                    style={{
                      opacity: r.skip || r.parse_error ? 0.5 : 1,
                      background: r.parse_error ? '#fef2f2' : r.duplicate_of_id ? '#fef3c7' : undefined,
                      boxShadow: `inset 3px 0 0 ${stripe}` }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={!r.skip}
                        disabled={!!r.parse_error}
                        onChange={(e) => updateRow(i, {
                          skip: !e.target.checked,
                          allow_duplicate: e.target.checked && !!r.duplicate_of_id,
                        })}
                      />
                    </td>
                    <td>
                      {r.line_number}
                      {/* ч.815 (рішення власника): лише «частково»/«дубль»; чого бракує —
                          видно за помаранчевою рамкою поля. */}
                      {(partial || r.duplicate_of_id) && (
                        <div className="cell-hint" style={{ color: 'var(--badge-amber-fg)', fontWeight: 600 }}>
                          {partial && <span title={`Не вистачає: ${reasons.join(', ')}`}>частково</span>}
                          {partial && r.duplicate_of_id && ' · '}
                          {r.duplicate_of_id && (
                            <span title={`Збігається з документом № ${r.duplicate_of_number}`}>⚠ дубль</span>)}
                        </div>
                      )}
                    </td>
                    <td>{new Date(r.date).toLocaleDateString('uk-UA')}</td>
                    <td style={{ fontWeight: 600 }}>
                      <span style={{ color: r.direction === 'IN' ? '#15803d' : '#b91c1c' }}>
                        {r.direction === 'IN' ? '↓' : '↑'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.amount)}</td>
                    <td>
                      {r.parse_error ? (
                        <span style={{ color: 'var(--badge-red-fg)' }}>⚠ {r.parse_error}</span>
                      ) : (r.operation_type === 'SALARY' || r.operation_type === 'TO_PODOTCHET') ? (
                        // ч.810: виплата працівнику — фізособа з картки працівника, без контрагента.
                        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}
                          title="Зарплата: отримувач — працівник (рах. 661), контрагент не потрібен">
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <SearchSelect
                              items={employeeItems}
                              value={r.employee_id ?? ''}
                              inputStyle={r.assist_fields?.includes('employee') ? { borderColor: AI_COLOR } : !r.employee_id ? { borderColor: MISSING } : undefined}
                              placeholder="працівник: пошук…"
                              onChange={(id) => updateRow(i, { employee_id: id || null, contractor_id: null, contract_id: null })}
                            />
                          </span>
                          {aiCheck(r, i, 'employee')}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <SearchSelect
                              items={contractorItems}
                              value={r.contractor_id ?? ''}
                              inputStyle={r.assist_fields?.includes('contractor') ? { borderColor: AI_COLOR } : !r.contractor_id ? { borderColor: MISSING } : undefined}
                              placeholder="контрагент: пошук…"
                              onChange={(id) => updateRow(i, {
                                contractor_id: id || null,
                                contract_id: null,
                                customer_order_id: null, supplier_order_id: null, matched_order_number: null,
                                basis_doc_id: null, basis_label: null,
                                recognized: !!id && !r.duplicate_of_id,
                                remember_rule: !!id,
                              })}
                            />
                          </span>
                          {aiCheck(r, i, 'contractor')}
                          {!r.contractor_id && (
                            <button type="button" style={{ padding: '0 0.4rem', flex: '0 0 auto' }}
                              title="Створити контрагента з реквізитів виписки"
                              onClick={() => setNewCtrFor(i)}>+</button>
                          )}
                        </div>
                      )}
                      {r.counterparty_name && (
                        <div className="cell-hint"
                          title={`${r.counterparty_name}${r.counterparty_edrpou ? ` · ${r.counterparty_edrpou}` : ''}`}>
                          {r.counterparty_name}
                          {r.counterparty_edrpou && ` · ${r.counterparty_edrpou}`}
                          {r.match_confidence === 'RULE' && (
                            <span style={{ color: '#2563eb', marginLeft: 4 }} title="Підставлено правилом">(правило)</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <select value={NO_CONTRACT_OPS.includes(r.operation_type) ? '' : (r.contract_id ?? '')}
                          onChange={(e) => updateRow(i, { contract_id: e.target.value || null })}
                          disabled={!r.contractor_id || NO_CONTRACT_OPS.includes(r.operation_type)}
                          title={NO_CONTRACT_OPS.includes(r.operation_type) ? 'Для цього виду операції договір не потрібен' : undefined}
                          style={{ width: '100%' }}>
                          <option value="">{ctrContracts.length === 0 && r.contractor_id ? '— (створиться авто) —' : '—'}</option>
                          {ctrContracts.map((c) => (
                            <option key={c.id} value={c.id}>{c.label}</option>
                          ))}
                        </select>
                        {r.contractor_id && !NO_CONTRACT_OPS.includes(r.operation_type) && (
                          <button type="button" style={{ padding: '0 0.4rem', flex: '0 0 auto' }}
                            title="Створити договір з цим контрагентом (назва, номер, дата; решта — у картці договору)"
                            onClick={() => setNewContractFor(i)}>+</button>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {!r.parse_error && (
                        <BasisPicker
                          orgId={orgId} contractorId={r.contractor_id ?? null} direction={r.direction}
                          orderId={r.customer_order_id ?? r.supplier_order_id ?? null}
                          basisDocId={r.basis_doc_id ?? null}
                          onPick={(pick) => updateRow(i, pick.kind === 'order'
                            ? { customer_order_id: r.direction === 'IN' ? pick.id : null,
                                supplier_order_id: r.direction === 'OUT' ? pick.id : null,
                                matched_order_number: pick.number, basis_doc_id: null, basis_label: null }
                            : pick.kind === 'doc'
                              ? { basis_doc_id: pick.id, basis_label: pick.number,
                                  customer_order_id: null, supplier_order_id: null, matched_order_number: null }
                              : { customer_order_id: null, supplier_order_id: null, matched_order_number: null,
                                  basis_doc_id: null, basis_label: null })}
                        />
                      )}
                      {r.bank_payment_id ? (
                        <div title="Наша збережена чернетка списання (/bank-payments → «збережені»): виписка ПІДТВЕРДИТЬ її — проведемо чернетку, дубля не буде">
                          списання № {r.matched_bank_payment_number} (чернетка)
                          <button type="button" style={{ marginLeft: 4, padding: '0 0.3rem' }}
                            title="Не повʼязувати з чернеткою (створити новий документ)"
                            onClick={() => updateRow(i, { bank_payment_id: null, matched_bank_payment_number: null })}>×</button>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <select
                          value={r.operation_type}
                          onChange={(e) => changeOp(i, e.target.value)}
                          style={{ width: '100%', borderColor: r.assist_fields?.includes('operation_type') ? AI_COLOR : !r.operation_type ? MISSING : undefined }}
                        >
                          <option value="">— не розпізнано —</option>
                          {rowOps(r.direction === 'IN' ? RECEIPT_OPS : PAYMENT_OPS, r.operation_type).map((op) => (
                            <option key={op} value={op}>{OP_LABEL[op] ?? op}</option>
                          ))}
                        </select>
                        {aiCheck(r, i, 'operation_type')}
                      </div>
                    </td>
                    <td>
                      {/* ч.815: стаття ДДС — пошуковий вибір. */}
                      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <SearchSelect
                            items={cfiSearchItems}
                            value={r.cash_flow_item_id ?? ''}
                            placeholder="ДДС: пошук…"
                            inputStyle={r.assist_fields?.includes('cfi') ? { borderColor: AI_COLOR } : !r.cash_flow_item_id ? { borderColor: MISSING } : undefined}
                            dropdownMinWidth={420}
                            onChange={(id) => updateRow(i, { cash_flow_item_id: id || null, cfi_auto: null })}
                          />
                        </span>
                        {aiCheck(r, i, 'cfi')}
                      </div>
                    </td>
                    <td>
                      <textarea
                        value={r.purpose ?? ''}
                        rows={2}
                        title={r.purpose ?? ''}
                        onChange={(e) => updateRow(i, { purpose: e.target.value })}
                        style={{ resize: 'vertical', lineHeight: 1.25 }}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={!!r.remember_rule}
                        disabled={!r.contractor_id || (!r.counterparty_edrpou && !r.counterparty_iban)}
                        title={!r.counterparty_edrpou && !r.counterparty_iban
                          ? 'У виписці немає ЄДРПОУ/IBAN контрагента — ключа для правила'
                          : undefined}
                        onChange={(e) => updateRow(i, { remember_rule: e.target.checked })} />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ч.815 (рішення власника): дії — на нижній статичній плашці; зліва
              «виділити» за статусом (галка = рядок піде у створення). */}
          <DocActionBar>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginRight: 4 }}>виділити:</span>
            <button type="button" onClick={() => selectBy('all')} title="Позначити всі рядки без помилок">усі</button>
            <button type="button" onClick={() => selectBy('full')} title="Позначити лише повністю розпізнані (зелена смужка)">розпізнані</button>
            <button type="button" onClick={() => selectBy('partial')} title="Позначити лише «частково» (є контрагент, бракує виду операції/ДДС)">частково</button>
            <button type="button" onClick={() => selectBy('dup')} title="Позначити лише можливі дублі">дубль</button>
            <button type="button" onClick={() => selectBy('none')} title="Зняти позначки з усіх">жодного</button>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
              <button
                className="danger"
                disabled={applyMut.isPending || activeRows.length === 0}
                onClick={() => void removeChecked()}
                title={isReview ? 'Видалити позначені галкою документи (чернетки)' : 'Прибрати позначені галкою рядки з переліку (документи по них не створюються)'}>
                видалити ({activeRows.length})
              </button>
              {/* ч.1231 (знахідка власника: «автопроводки вимкнено, а документи проведені»):
                  виписка з підключення банку, у якого вимкнено «Автопроводки розпізнаних», —
                  основна дія створює чернетки; проведення лишається окремою кнопкою. */}
              <button
                className={draftsFirst ? 'primary' : undefined}
                disabled={applyMut.isPending || activeRows.length === 0}
                onClick={() => runApply(false)}
                title={isReview ? 'Зберегти зміни у позначених документах (без проведення)'
                  : draftsFirst ? 'У налаштуваннях підключення банку «Автопроводки розпізнаних» вимкнено — документи створюються чернетками, проведете самі'
                  : 'Створити всі позначені рядки чернетками (проведете пізніше)'}>
                {isReview ? 'зберегти' : 'створити чернетками'} ({activeRows.length})
              </button>
              <button
                className={draftsFirst ? undefined : 'primary'}
                disabled={applyMut.isPending || activeRows.length === 0}
                onClick={() => runApply(true)}
                title="Створити всі позначені рядки; повністю розпізнані (зелена смужка) — одразу провести, решта лишаться чернетками">
                {applyMut.isPending ? (isReview ? 'Збереження…' : 'Створення…') : isReview ? `зберегти і провести розпізнані (${recognizedActive})` : `створити і провести (${recognizedActive})`}
              </button>
            </span>
          </DocActionBar>
        </>
      )}

      {newCtrFor !== null && rows[newCtrFor] && (
        <QuickContractorModal
          row={rows[newCtrFor]}
          onClose={() => setNewCtrFor(null)}
          onCreated={(id) => {
            const idx = newCtrFor;
            qc.invalidateQueries({ queryKey: ['contractors-all'] });
            setNewCtrFor(null);
            updateRow(idx, { contractor_id: id, contract_id: null, recognized: !rows[idx]?.duplicate_of_id, remember_rule: true });
          }}
        />
      )}

      {newContractFor !== null && rows[newContractFor]?.contractor_id && (
        <QuickContractModal
          orgId={orgId}
          contractorId={rows[newContractFor]!.contractor_id!}
          contractorName={contractorsList.find((c) => c.id === rows[newContractFor]!.contractor_id)?.name ?? ''}
          direction={rows[newContractFor]!.direction}
          operationType={rows[newContractFor]!.operation_type}
          onClose={() => setNewContractFor(null)}
          onCreated={(id) => {
            const idx = newContractFor;
            qc.invalidateQueries({ queryKey: ['contracts-org', orgId] });
            setNewContractFor(null);
            updateRow(idx, { contract_id: id });
          }}
        />
      )}

      {applyResult && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: applyResult.errors.length ? '#fef3c7' : '#dcfce7',
          border: `1px solid ${applyResult.errors.length ? '#fbbf24' : '#86efac'}`,
          borderRadius: 6 }}>
          <strong>Результат імпорту</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
            {(applyResult.reviewed ?? 0) > 0 && <li>Оновлено документів: {applyResult.reviewed}</li>}
            {!isReview && <li>Створено надходжень: {applyResult.created_receipts}</li>}
            {!isReview && <li>Створено списань: {applyResult.created_payments}</li>}
            <li>Проведено: {applyResult.posted}</li>
            {(applyResult.bank_payments_confirmed ?? 0) > 0 && (
              <li>Підтверджено збережених списань (чернеток): {applyResult.bank_payments_confirmed}</li>
            )}
            {applyResult.rules_saved > 0 && (
              <li>Запамʼятовано правил: {applyResult.rules_saved}</li>
            )}
            <li>Пропущено: {applyResult.skipped}</li>
            {applyResult.skipped_as_duplicate > 0 && (
              <li style={{ color: 'var(--badge-amber-fg)' }}>
                Пропущено як дублі (захист сервера): {applyResult.skipped_as_duplicate}
              </li>
            )}
            {applyResult.errors.length > 0 && (
              <li style={{ color: 'var(--badge-red-fg)' }}>
                Помилок: {applyResult.errors.length}
                <ul>
                  {applyResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function statementRowToState(r: StatementRow): RowState {
  return {
    line_number: r.line_number,
    skip: r.parse_error !== null || r.duplicate_of_id !== null,
    allow_duplicate: false,
    direction: r.direction,
    date: r.date,
    amount: r.amount,
    review_doc_id: r.review_doc_id ?? null,  // ч.833
    contractor_id: r.matched_contractor_id,
    employee_id: r.matched_employee_id ?? null,
    matched_employee_name: r.matched_employee_name ?? null,
    contract_id: r.matched_contract_id,
    operation_type: r.target_operation_type ?? '',  // ч.830: не розпізнано → порожньо
    purpose: r.purpose,
    document_number: r.doc_number,
    incoming_doc_number: r.doc_number,
    incoming_doc_date: r.doc_date,
    payer_name: r.direction === 'IN' ? r.counterparty_name : null,
    payer_edrpou: r.direction === 'IN' ? r.counterparty_edrpou : null,
    recipient_name: r.direction === 'OUT' ? r.counterparty_name : null,
    recipient_edrpou: r.direction === 'OUT' ? r.counterparty_edrpou : null,
    cash_flow_item_id: r.suggested_cash_flow_item_id,
    customer_order_id: r.matched_order_kind === 'customer' ? r.matched_order_id : null,
    supplier_order_id: r.matched_order_kind === 'supplier' ? r.matched_order_id : null,
    payment_order_id: r.matched_payment_order_id,
    bank_payment_id: r.matched_bank_payment_id ?? null,
    counterparty_edrpou: r.counterparty_edrpou,
    counterparty_iban: r.counterparty_iban,
    // Розпізнане без правила — запамʼятати за замовчуванням (памʼять росте сама).
    remember_rule: r.recognized && !r.rule_id && !!(r.counterparty_edrpou || r.counterparty_iban),
    post: false,
    bank_ref: r.bank_ref ?? null,
    parse_error: r.parse_error,
    match_confidence: r.match_confidence,
    counterparty_name: r.counterparty_name,
    duplicate_of_id: r.duplicate_of_id,
    duplicate_of_kind: r.duplicate_of_kind,
    duplicate_of_number: r.duplicate_of_number,
    recognized: r.recognized,
    matched_contract_name: r.matched_contract_name,
    matched_order_number: r.matched_order_number,
    matched_payment_order_number: r.matched_payment_order_number,
    matched_bank_payment_number: r.matched_bank_payment_number ?? null,
    rule_id: r.rule_id,
    basis_doc_id: null,
    basis_label: null,
    cfi_auto: null,
  };
}

/** ч.790: «Підстава» — пошук по відкритих замовленнях/рахунках і проведених
 * неоплачених накладних контрагента (як «На підставі» у платіжці). */
function BasisPicker({ orgId, contractorId, direction, orderId, basisDocId, onPick }: {
  orgId: string; contractorId: string | null; direction: 'IN' | 'OUT';
  orderId: string | null; basisDocId: string | null;
  onPick: (p: { kind: 'order' | 'doc'; id: string; number: string } | { kind: 'none' }) => void;
}) {
  const ordersApi = direction === 'IN' ? customerOrders : supplierOrders;
  const ordersQ = useQuery({
    queryKey: ['import-basis-orders', direction, orgId, contractorId],
    queryFn: () => ordersApi.list({ organization_id: orgId, contractor_id: contractorId!, posted: true, limit: 100 }),
    enabled: !!orgId && !!contractorId, staleTime: 60_000,
  });
  const unpaidQ = useQuery({
    queryKey: ['import-basis-unpaid', direction, orgId, contractorId],
    queryFn: () => reportsApi.settlementsByDocuments({
      organization_id: orgId, contractor_id: contractorId!,
      side: direction === 'IN' ? 'receivable' : 'payable', limit: 200,
    }),
    enabled: !!orgId && !!contractorId, staleTime: 60_000,
  });
  const fmtD = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString('uk-UA') : '');
  const items = [
    ...(ordersQ.data?.items ?? [])
      .filter((o) => ((o as { closing_status?: string }).closing_status ?? 'OPEN') !== 'CLOSED')
      .map((o) => ({
        id: `order:${o.id}`,
        label: `${o.order_kind === 'PROFORMA_INVOICE' ? 'Рахунок' : 'Замовлення'} ${o.document_number} від ${fmtD(o.document_date)}`,
        hint: `${Number(o.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн`,
      })),
    ...(unpaidQ.data?.items ?? []).map((r: SettlementByDocumentRow) => ({
      id: `doc:${r.settlement_doc_id}`,
      label: `${direction === 'IN' ? 'Реалізація' : 'Надходження'} ${r.document_number ?? ''}${r.document_date ? ' від ' + fmtD(r.document_date) : ''}`,
      hint: `не сплачено ${Number(r.open_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн`,
    })),
  ];
  const value = orderId ? `order:${orderId}` : basisDocId ? `doc:${basisDocId}` : '';
  if (!contractorId) return <span style={{ color: 'var(--muted)' }}>— оберіть контрагента —</span>;
  return (
    <SearchSelect
      items={items}
      value={value}
      placeholder={items.length ? 'підстава: пошук…' : 'неоплачених документів немає'}
      onChange={(v) => {
        if (!v) { onPick({ kind: 'none' }); return; }
        const kind = v.slice(0, v.indexOf(':')) as 'order' | 'doc';
        const id = v.slice(v.indexOf(':') + 1);
        const it = items.find((x) => x.id === v);
        onPick({ kind, id, number: it?.label ?? '' });
      }}
    />
  );
}

/** ч.790: швидке створення контрагента з реквізитів рядка виписки. IBAN у
 * картку контрагента запишеться при «створити» (apply, ч.755). */
function QuickContractorModal({ row, onClose, onCreated }: {
  row: RowState; onClose: () => void; onCreated: (id: string) => void;
}) {
  const code = (row.counterparty_edrpou ?? '').replace(/\D/g, '');
  const guessType: ContractorType = code.length === 10 ? 'INDIVIDUAL_FOP' : code.length === 8 ? 'LEGAL' : 'INDIVIDUAL';
  const [name, setName] = useState(row.counterparty_name ?? '');
  const [ctype, setCtype] = useState<ContractorType>(guessType);
  const [codeVal, setCodeVal] = useState(code);
  const [isBuyer, setIsBuyer] = useState(row.direction === 'IN');
  const [isSupplier, setIsSupplier] = useState(row.direction === 'OUT');
  const [err, setErr] = useState<string | null>(null);
  const [similar, setSimilar] = useState(0);   // ч.1237: скільки схожих уже є в довіднику
  const create = useMutation({
    mutationFn: () => {
      const payload: ContractorCreate = {
        code: null, name: name.trim(), full_name: null, contractor_type: ctype,
        edrpou: ctype === 'LEGAL' || ctype === 'SEPARATE_DIVISION' ? (codeVal || null) : null,
        inn: ctype === 'INDIVIDUAL_FOP' || ctype === 'INDIVIDUAL' ? (codeVal || null) : null,
        is_vat_payer: false, vat_number: null, tax_system: null,
        legal_address: null, actual_address: null, phone: null, email: null,
        is_buyer: isBuyer, is_supplier: isSupplier, is_other: !isBuyer && !isSupplier,
        is_non_resident: false, group_id: null, kved_code: null,
      };
      return contractorsApi.create(payload);
    },
    onSuccess: (c) => onCreated(c.id),
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : Array.isArray(d) ? d.map((x: { msg?: string }) => x.msg).join('; ') : 'Не вдалося створити контрагента');
    },
  });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div className="card" style={{ width: 520, maxWidth: '95vw', padding: '1rem' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 0.6rem' }}>Новий контрагент з виписки</h3>
        <div className="field"><label>Назва *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
        {/* ч.1237 (знахідка власника): такий контрагент уже є — за кодом, рахунком або назвою
            з виписки; пропонуємо обрати його, а не заводити дубль. */}
        <ContractorCodeHint code={codeVal} name={name} iban={row.counterparty_iban}
          onMatches={setSimilar}
          onPick={(m) => onCreated(m.id)}
          onOpen={(m) => window.open(`/contractors/${m.id}`, '_blank', 'noopener')} />
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}><label>Тип</label>
            <select value={ctype} onChange={(e) => setCtype(e.target.value as ContractorType)}>
              <option value="LEGAL">Юридична особа</option>
              <option value="INDIVIDUAL_FOP">ФОП</option>
              <option value="INDIVIDUAL">Фізична особа</option>
              <option value="SEPARATE_DIVISION">Відокремлений підрозділ</option>
            </select></div>
          <div className="field" style={{ gridColumn: 'span 6' }}><label>{ctype === 'LEGAL' || ctype === 'SEPARATE_DIVISION' ? 'ЄДРПОУ' : 'РНОКПП'}</label>
            <input value={codeVal} onChange={(e) => setCodeVal(e.target.value.trim())} /></div>
        </div>
        <div style={{ display: 'flex', gap: '1rem', margin: '0.3rem 0 0.6rem' }}>
          <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={isBuyer} onChange={(e) => setIsBuyer(e.target.checked)} style={{ width: 'auto', marginRight: 4 }} />покупець</label>
          <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={isSupplier} onChange={(e) => setIsSupplier(e.target.checked)} style={{ width: 'auto', marginRight: 4 }} />постачальник</label>
        </div>
        {row.counterparty_iban && (
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
            IBAN {row.counterparty_iban} буде додано в картку при створенні документів.
          </div>
        )}
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose}>скасувати</button>
          <button type="button" className="primary" disabled={create.isPending || name.trim().length < 2}
            onClick={() => create.mutate()}>
            {create.isPending ? 'створення…' : similar > 0 ? 'усе одно створити' : 'створити'}</button>
        </div>
      </div>
    </div>
  );
}
